import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ApplicationService } from '../../services/application.service';
import { TranslationService } from '../../services/translation.service';
import type { JobInvitationTranslations } from '../../i18n/job-invitation';
import { getJobInvitationStatusOptions, getJobInvitationStatusLabel } from '../../models/job-invitation-status.model';
import { parseUtcTimestamp } from '../../utils/elapsed-time.util';
import en from '../../i18n/en/job-invitation';

@Component({
  selector: 'app-job-invitation',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './job-invitation.component.html',
  styleUrls: ['./job-invitation.component.css']
})
export class JobInvitationComponent implements OnInit {
  @Input() applicationId!: number;
  @Input() jobTitle?: string;
  @Input() companyName?: string;
  @Output() invitationUpdated = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  invitations: any[] = [];
  isLoading = false;
  isSubmitting = false;
  error: string | null = null;
  invitationForm: FormGroup;
  editingInvitationId: number | null = null;

  translations: JobInvitationTranslations = en as JobInvitationTranslations;

  get t(): JobInvitationTranslations {
    return this.translations;
  }

  statusOptions: Array<{value: string, label: string}> = [];

  constructor(
    private appService: ApplicationService,
    private fb: FormBuilder,
    private translationService: TranslationService
  ) {
    this.invitationForm = this.fb.group({
      invited_at: [null],
      status: ['pending', Validators.required],
      notes: ['']
    });

    const current = this.translationService.getJobInvitationTranslations();
    if (current) {
      this.translations = current;
    }
    this.updateStatusOptions();
    this.translationService.onChange(() => {
      const next = this.translationService.getJobInvitationTranslations();
      if (next) {
        this.translations = next;
        this.updateStatusOptions();
      }
    });
  }

  private updateStatusOptions(): void {
    this.statusOptions = getJobInvitationStatusOptions(this.t);
  }

  getStatusLabel(status: string | null | undefined): string {
    return getJobInvitationStatusLabel(status, this.t);
  }

  // chamba-ai-backend-fastapi#186: invited_at is a backend-sourced naive-UTC
  // timestamp (no "Z"/offset suffix). Angular's DatePipe defers to
  // `new Date(value)` for a full ISO datetime string with no offset, which
  // parses it as the BROWSER'S LOCAL time instead of UTC - the same
  // elapsed-time.util.ts::parseUtcTimestamp() bug PR #345 already fixed at
  // every literal `new Date(` call site, missed here because this call
  // site is a template pipe, not a literal `new Date(` (PR #345's own
  // sweep only grepped for that). Route through parseUtcTimestamp() before
  // the `date` pipe gets it, same as every other display call site.
  toDisplayDate(isoString: string | null | undefined): Date | null {
    return isoString ? parseUtcTimestamp(isoString) : null;
  }

  ngOnInit(): void {
    this.loadInvitations();
  }

  loadInvitations() {
    this.isLoading = true;
    this.appService.listInvitations(this.applicationId).subscribe({
      next: (data) => {
        this.invitations = data;
        this.isLoading = false;
      },
      error: () => {
        this.error = this.t.ERROR_FAILED_TO_LOAD;
        this.isLoading = false;
      }
    });
  }

  onSubmit() {
    if (this.invitationForm.invalid) {
      this.invitationForm.markAllAsTouched();
      return;
    }
    this.isSubmitting = true;
    // Convert invited_at to ISO string if present and not empty
    const formValue = { ...this.invitationForm.value };
    if (formValue.invited_at) {
      formValue.invited_at = new Date(formValue.invited_at).toISOString();
    } else {
      formValue.invited_at = null;
    }

    if (this.editingInvitationId) {
      // Update existing invitation
      this.appService.updateInvitation(this.editingInvitationId, formValue).subscribe({
        next: () => {
          this.isSubmitting = false;
          this.editingInvitationId = null;
          this.invitationForm.reset({ status: 'pending' });
          this.loadInvitations();
          this.invitationUpdated.emit();
        },
        error: () => {
          this.isSubmitting = false;
        }
      });
    } else {
      // Create new invitation
      this.appService.createInvitation(this.applicationId, formValue).subscribe({
        next: () => {
          this.isSubmitting = false;
          this.invitationForm.reset({ status: 'pending' });
          this.loadInvitations();
          this.invitationUpdated.emit();
        },
        error: () => {
          this.isSubmitting = false;
        }
      });
    }
  }

  editInvitation(invitation: any) {
    this.editingInvitationId = invitation.id;
    let invitedAt = invitation.invited_at;
    if (invitedAt) {
      // chamba-ai-backend-fastapi#186: invitation.invited_at is a naive-UTC
      // backend timestamp - parseUtcTimestamp() gives the correct instant
      // regardless of the browser's offset (see toDisplayDate() above).
      //
      // Convert that instant to the "YYYY-MM-DDTHH:mm" format a
      // <input type="datetime-local"> expects, which the browser always
      // interprets/displays as LOCAL wall-clock time - so this must be
      // built from LOCAL getters (getFullYear/getMonth/.../getMinutes),
      // never `.toISOString()` (always UTC-formatted, regardless of the
      // browser's timezone). Using toISOString() here was the bug: it fed
      // a UTC-formatted string into a field the browser displays as local
      // time, showing the wrong wall-clock time by exactly the browser's
      // UTC offset - and, before parseUtcTimestamp() existed, compounded
      // with the naive-string misparse into a DOUBLE shift on every
      // edit-then-resave round trip.
      const date = parseUtcTimestamp(invitedAt);
      const pad = (n: number) => String(n).padStart(2, '0');
      invitedAt =
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }
    this.invitationForm.patchValue({
      invited_at: invitedAt,
      status: invitation.status,
      notes: invitation.notes
    });
  }

  cancelEdit() {
    this.editingInvitationId = null;
    this.invitationForm.reset({ status: 'pending' });
    this.cancel.emit();
  }
}
